'use client';

import React, { createContext, ReactNode } from 'react';

// Browser-safe VibeshipConfig type
export interface VibeshipConfig {
  scanPaths: string[];
  include: string[];
  exclude: string[];
  features: {
    ai?: boolean;
    tasks?: boolean;
    fileEditing?: boolean;
    commandPalette?: boolean;
    devtools?: boolean;
    realtime?: boolean;
    markdownPreview?: boolean;
  };
  ui: {
    theme: 'light' | 'dark' | 'system';
    hotkey: string;
    position: string;
    showInProduction: boolean;
    defaultSize?: { width: number; height: number };
  };
  api: {
    basePath: string;
    timeout: number;
    version?: string;
    documentation?: boolean;
    middleware?: any[];
  };
  cache: {
    enabled: boolean;
    ttl: number;
    maxSize: number;
    directory?: string;
    strategy?: string;
  };
  security?: {
    authentication: boolean;
    rateLimit: boolean;
    cors: boolean;
    allowedPaths: string[];
  };
  taskPatterns?: {
    todo: boolean;
    fixme: boolean;
    hack: boolean;
    note: boolean;
  };
  experimental: Record<string, any>;
}

export interface VibeshipContextValue {
  config: VibeshipConfig;
  isReady: boolean;
}

export const VibeshipContext = createContext<VibeshipContextValue | undefined>(undefined);

interface VibeshipProviderProps {
  children: ReactNode;
  config?: Partial<VibeshipConfig>;
}

export function VibeshipProvider({ children, config = {} }: VibeshipProviderProps) {
  // This would be imported from the actual provider in @vibeship/ui
  // For now, we're creating a minimal version
  const value: VibeshipContextValue = {
    config: {
      scanPaths: config.scanPaths || ['./src', './docs', './tickets'],
      include: config.include || ['**/*.md', '**/*.mdx', '**/*.ts', '**/*.tsx'],
      exclude: config.exclude || ['node_modules', '.next', 'dist'],
      features: {
        tasks: config.features?.tasks ?? true,
        ai: config.features?.ai ?? true,
        realtime: config.features?.realtime ?? true,
        commandPalette: config.features?.commandPalette ?? true,
        fileEditing: config.features?.fileEditing ?? false,
        markdownPreview: config.features?.markdownPreview ?? true,
        ...config.features,
      },
      security: {
        authentication: config.security?.authentication ?? false,
        rateLimit: config.security?.rateLimit ?? true,
        cors: config.security?.cors ?? true,
        allowedPaths: config.security?.allowedPaths || ['./src', './docs'],
        ...config.security,
      },
      taskPatterns: {
        todo: config.taskPatterns?.todo ?? true,
        fixme: config.taskPatterns?.fixme ?? true,
        hack: config.taskPatterns?.hack ?? true,
        note: config.taskPatterns?.note ?? true,
        ...config.taskPatterns,
      },
      ui: {
        theme: config.ui?.theme || 'system',
        position: config.ui?.position || 'bottom-right',
        defaultSize: config.ui?.defaultSize || { width: 400, height: 600 },
        hotkey: config.ui?.hotkey || 'cmd+k',
        showInProduction: config.ui?.showInProduction ?? false,
        ...config.ui,
      },
      api: {
        basePath: config.api?.basePath || '/api/vibecode',
        version: config.api?.version || 'v1',
        documentation: config.api?.documentation ?? true,
        timeout: config.api?.timeout || 30000,
        middleware: config.api?.middleware || [],
        ...config.api,
      },
      cache: {
        enabled: config.cache?.enabled ?? true,
        directory: config.cache?.directory || './.vibeship/cache',
        ttl: config.cache?.ttl || 300,
        maxSize: config.cache?.maxSize || 100,
        strategy: config.cache?.strategy || 'lru',
        ...config.cache,
      },
      experimental: config.experimental || {},
    },
    isReady: true,
  };

  return (
    <VibeshipContext.Provider value={value}>
      {children}
    </VibeshipContext.Provider>
  );
}